import React, { useEffect, useRef, useState } from "react";
import useConfirm from "./ConfirmDialog";

// Dialog for a project's shared sections. Changes apply immediately for
// everyone on the project; deleting a section moves its tasks to "No section".
export default function ProjectSectionsEditor({ manager, onClose }) {
  const { sections, error } = manager;
  const [names, setNames] = useState({});
  const [newName, setNewName] = useState("");
  const dialogRef = useRef(null);
  const [confirm, confirmDialog] = useConfirm();

  useEffect(() => {
    dialogRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const nameOf = (s) => (s.id in names ? names[s.id] : s.name);

  const commitRename = (s) => {
    const next = nameOf(s).trim();
    if (!next || next === s.name) {
      setNames(({ [s.id]: _drop, ...rest }) => rest);
      return;
    }
    manager.renameSection(s.id, next);
  };

  const move = (index, delta) => {
    const ids = sections.map((s) => s.id);
    const [id] = ids.splice(index, 1);
    ids.splice(index + delta, 0, id);
    manager.reorderSections(ids);
  };

  const add = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    if (await manager.addSection(newName.trim())) setNewName("");
  };

  return (
    <div
      className="tk-modal-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="tk-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tk-project-sections-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        <div className="tk-modal-head">
          <h2 id="tk-project-sections-title">Project sections</h2>
          <button
            type="button"
            className="tk-icon-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <p className="tk-muted tk-modal-intro">
          Shared by everyone on this project — they're the board's columns.
          Moving a task between sections doesn't change its dates.
        </p>

        {sections.length === 0 ? (
          <p className="tk-muted">
            No sections yet. Add one below, e.g. "To do", "Doing", "Done".
          </p>
        ) : (
          <ol className="tk-section-editor">
            {sections.map((s, index) => (
              <li key={s.id}>
                <div className="tk-se-order">
                  <button
                    type="button"
                    className="tk-icon-btn tk-icon-btn-sm"
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    aria-label={`Move ${s.name} up`}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="tk-icon-btn tk-icon-btn-sm"
                    onClick={() => move(index, 1)}
                    disabled={index === sections.length - 1}
                    aria-label={`Move ${s.name} down`}
                  >
                    ↓
                  </button>
                </div>
                <div className="tk-se-main">
                  <input
                    className="tk-input"
                    value={nameOf(s)}
                    maxLength={60}
                    onChange={(e) =>
                      setNames((prev) => ({ ...prev, [s.id]: e.target.value }))
                    }
                    onBlur={() => commitRename(s)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && e.currentTarget.blur()
                    }
                    aria-label="Section name"
                  />
                </div>
                <button
                  type="button"
                  className="tk-icon-btn tk-icon-btn-sm"
                  onClick={async () => {
                    const ok = await confirm({
                      title: "Delete this section?",
                      subject: s.name,
                      message:
                        'Its tasks aren\'t deleted — they move to "No section" for everyone on the project.',
                      confirmLabel: "Delete section",
                    });
                    if (ok) manager.deleteSection(s.id);
                  }}
                  aria-label={`Delete ${s.name}`}
                  title="Delete section"
                >
                  ×
                </button>
              </li>
            ))}
          </ol>
        )}

        <form className="tk-se-add" onSubmit={add}>
          <input
            className="tk-input"
            placeholder="New section name"
            value={newName}
            maxLength={60}
            onChange={(e) => setNewName(e.target.value)}
            aria-label="New section name"
          />
          <button
            type="submit"
            className="btn-secondary btn-sm"
            disabled={!newName.trim()}
          >
            Add section
          </button>
        </form>

        {error && <p className="tk-error">{error}</p>}

        <div className="tk-modal-actions">
          <span />
          <button type="button" className="btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
      {confirmDialog}
    </div>
  );
}
